import React, { useState, useRef, useEffect, useMemo } from 'react'

export interface SelectableComponentOption {
  id: string
  code: string
  name: string
  unit?: string
  maliyet?: number | string
  typeLabel?: string
  label?: string
}

interface Props {
  id?: string
  options: SelectableComponentOption[]
  value: string
  onChange: (id: string) => void
  placeholder?: string
  disabled?: boolean
  required?: boolean
  autoFocus?: boolean
}

function normalizeSearchText(text: string): string {
  return text
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .trim()
}

export function SearchableComponentSelect({
  id,
  options,
  value,
  onChange,
  placeholder = 'Bileşen adı veya kodu ile ara…',
  disabled = false,
  required = false,
  autoFocus = false,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const selectedOption = useMemo(
    () => options.find((opt) => opt.id === value),
    [options, value]
  )

  // Sync display search term when value changes or when selected option changes
  useEffect(() => {
    if (selectedOption) {
      setSearchTerm(`${selectedOption.code} — ${selectedOption.name}`)
    } else {
      setSearchTerm('')
    }
  }, [selectedOption, value])

  const isSearching = useMemo(() => {
    if (!isOpen) return false
    if (!selectedOption) return searchTerm.trim().length > 0
    const selectedText = `${selectedOption.code} — ${selectedOption.name}`
    return searchTerm.trim() !== selectedText.trim() && searchTerm.trim().length > 0
  }, [isOpen, selectedOption, searchTerm])

  const filteredOptions = useMemo(() => {
    if (!isSearching || !searchTerm.trim()) return options
    const term = searchTerm.trim()
    const normTerm = normalizeSearchText(term)
    const lowerTerm = term.toLocaleLowerCase('tr-TR')

    return options.filter((opt) => {
      const nameLower = opt.name.toLocaleLowerCase('tr-TR')
      const codeLower = opt.code.toLocaleLowerCase('tr-TR')
      if (nameLower.includes(lowerTerm) || codeLower.includes(lowerTerm)) {
        return true
      }
      const nameNorm = normalizeSearchText(opt.name)
      const codeNorm = normalizeSearchText(opt.code)
      return nameNorm.includes(normTerm) || codeNorm.includes(normTerm)
    })
  }, [options, isSearching, searchTerm])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        // Reset input text back to selected item if left unselected
        if (selectedOption) {
          setSearchTerm(`${selectedOption.code} — ${selectedOption.name}`)
        } else {
          setSearchTerm('')
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [selectedOption])

  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length) {
      setHighlightedIndex(Math.max(0, filteredOptions.length - 1))
    }
  }, [filteredOptions.length, highlightedIndex])

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement
      if (el) {
        el.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [isOpen, highlightedIndex])

  const handleSelect = (option: SelectableComponentOption) => {
    onChange(option.id)
    setSearchTerm(`${option.code} — ${option.name}`)
    setIsOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange('')
    setSearchTerm('')
    setIsOpen(true)
    inputRef.current?.focus()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchTerm(val)
    setHighlightedIndex(0)
    if (!isOpen) setIsOpen(true)
    if (!val.trim()) {
      onChange('')
    }
  }

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsOpen(true)
    if (selectedOption) {
      e.target.select()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      setIsOpen(false)
      if (selectedOption) {
        setSearchTerm(`${selectedOption.code} — ${selectedOption.name}`)
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0))
    } else if (e.key === 'Enter') {
      if (filteredOptions[highlightedIndex]) {
        e.preventDefault()
        handleSelect(filteredOptions[highlightedIndex])
      }
    }
  }

  return (
    <div
      ref={containerRef}
      className={`searchable-select-container ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
      style={{ position: 'relative', width: '100%' }}
    >
      {/* Hidden input for HTML form validation */}
      <input
        type="text"
        id={id}
        name={id}
        tabIndex={-1}
        required={required}
        value={value || ''}
        onChange={() => {}}
        style={{
          opacity: 0,
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      />

      {/* Direct Search Input Field */}
      <div
        className="searchable-select-input-wrapper"
        onClick={() => {
          if (!disabled) {
            setIsOpen(true)
            inputRef.current?.focus()
          }
        }}
      >
        <span className="searchable-select-search-icon" aria-hidden="true">
          🔍
        </span>

        <input
          ref={inputRef}
          type="text"
          className="searchable-select-direct-input"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />

        <div className="searchable-select-actions">
          {value && !disabled && (
            <button
              type="button"
              className="searchable-select-clear-btn"
              onClick={handleClear}
              title="Seçimi temizle"
              aria-label="Seçimi temizle"
            >
              ✕
            </button>
          )}
          <span
            className={`searchable-select-caret ${isOpen ? 'open' : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled) {
                setIsOpen((prev) => !prev)
                inputRef.current?.focus()
              }
            }}
          >
            ▼
          </span>
        </div>
      </div>

      {/* Dropdown Options List */}
      {isOpen && (
        <div className="searchable-select-dropdown" role="listbox">
          <ul ref={listRef} className="searchable-select-list">
            {filteredOptions.length === 0 ? (
              <li className="searchable-select-empty">
                <span>"{searchTerm}" ile eşleşen bileşen bulunamadı.</span>
              </li>
            ) : (
              filteredOptions.map((opt, index) => {
                const isSelected = opt.id === value
                const isHighlighted = index === highlightedIndex

                return (
                  <li
                    key={opt.id}
                    role="option"
                    aria-selected={isSelected}
                    className={`searchable-select-option ${isSelected ? 'is-selected' : ''} ${isHighlighted ? 'is-highlighted' : ''}`}
                    onClick={() => handleSelect(opt)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <div className="searchable-select-opt-left">
                      <span className="searchable-select-code-badge">{opt.code}</span>
                      <span className="searchable-select-opt-name">{opt.name}</span>
                    </div>

                    <div className="searchable-select-opt-right">
                      <span className="searchable-select-opt-cost tabular-nums">
                        ₺{Number(opt.maliyet || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        <span className="text-muted"> / {opt.unit || 'Birim'}</span>
                      </span>
                      {isSelected && <span className="searchable-select-check">✓</span>}
                    </div>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
